<script setup>
// Pay now for a booking still waiting on a card (BE3, R4 NH1): the saved card, the hold line, 3-D Secure finished
// in the Stripe SDK then pay/sync. Grant 2026-11-02: lifted out of MyBookingsList so the booking's own page
// (/your-profile/bookings/{id}) and the list run the one dialog.
import {ref, watch} from 'vue';
import {plainStripeError} from '~/utils/stripeErrors.js';
import {useBookingsMember, bookingApiError} from '~/composables/useBookingsMember';
import {heldUntilLine, money, outcomeStep, owesExtra, payAmount} from '~/utils/bookingsMember';
import MembershipSavedCardRow from '~/components/memberships/MembershipSavedCardRow.vue';
import TlDialog from '~/components/TlDialog.vue';

const props = defineProps({
  modelValue: {type: Boolean, default: false},
  /** The booking to pay for. */
  booking: {type: Object, default: null},
});
const emit = defineEmits(['update:modelValue', 'paid']);

const api = useBookingsMember();
const {setAlertMessage} = useAlertService();

const cardId = ref(null);
const hasCard = ref(false);
const busy = ref(false);
const errors = ref({});
watch(() => props.modelValue, (open) => { if (open) errors.value = {}; });

const close = () => emit('update:modelValue', false);
const settle = async (res) => {
  const step = outcomeStep(res);
  if (step === 'act' && res.clientSecret) {
    const {error} = await api.finishCardAction({clientSecret: res.clientSecret, stripeAccountId: res.stripeAccountId});
    if (error) { errors.value = {global: [plainStripeError(error)]}; return false; }
    return settle(await api.paySync(res.id));
  }
  if (step === 'failed') { errors.value = {global: [res.message || "Your card didn't go through. Try another card."]}; return false; }
  setAlertMessage(res.message || (step === 'done' ? (owesExtra(props.booking) ? 'Paid. Thanks.' : 'Paid. Your booking is confirmed.') : "We're confirming your payment."));
  return true;
};
const payNow = async () => {
  if (!props.booking) return;
  busy.value = true;
  errors.value = {};
  try {
    if (await settle(await api.pay(props.booking.id, cardId.value))) {
      close();
      emit('paid');
    }
  } catch (e) {
    errors.value = {global: [bookingApiError(e, "The payment couldn't be taken.")]};
  } finally {
    busy.value = false;
  }
};
</script>

<template>
  <tl-dialog :model-value="modelValue" :title="booking && owesExtra(booking) ? 'Pay for the extra time' : 'Pay for your booking'" :max-width="420" @update:model-value="emit('update:modelValue', $event)">
    <div v-if="booking" class="bpd">
      <div class="bpd-title">{{ booking.line }}</div>
      <div class="bpd-sub">{{ money(payAmount(booking), booking.currency) }} · {{ heldUntilLine(booking) }}</div>
      <div v-if="booking.shareStatusLine || booking.shareLine" class="bpd-sub">{{ booking.shareStatusLine || booking.shareLine }}</div>
      <div class="tl-label mt-4 mb-1">Your card</div>
      <membership-saved-card-row @change="cardId = $event" @loaded="hasCard = $event.hasCard"/>
      <crud-error-display class="mt-3" :errors="errors"/>
    </div>
    <template #actions>
      <v-btn variant="outlined" :disabled="busy" @click="close">Later</v-btn>
      <v-btn color="primary" prepend-icon="mdi-lock" :loading="busy" :disabled="!hasCard" @click="payNow">Pay {{ booking ? money(payAmount(booking), booking.currency) : '' }}</v-btn>
    </template>
  </tl-dialog>
</template>

<style scoped lang="scss">
.bpd { font-size: 13px; }
.bpd-title { font-size: 13.5px; font-weight: 700; letter-spacing: -0.2px; }
.bpd-sub { font-size: 12px; color: rgb(var(--v-theme-textSubtle)); margin-top: 2px; line-height: 1.4; }
</style>
